import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MARKETING_CODES, PRODUCT_COMPANY_GROUPS } from "../../../constants/ProductConstant";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Amsterdam");

const useReportFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to validate company filter
  const isValidCompany = (company) => {
    return company === "All" || PRODUCT_COMPANY_GROUPS.some((group) => group.value === company);
  };

  // Helper function to validate marketing code filter
  const isValidMarketingCode = (code) => {
    return code === "All" || MARKETING_CODES.some((c) => c.value === code);
  };

  // Helper function to validate date string
  const isValidDate = (dateString) => {
    return dateString && dayjs(dateString, "YYYY-MM-DD", true).isValid();
  };

  // Helper function to validate week offset
  const isValidWeekOffset = (offset) => {
    const num = parseInt(offset);
    return !isNaN(num) && num >= -52 && num <= 52; // Allow up to 1 year offset
  };

  // Parse and validate URL parameters
  const parseFiltersFromURL = () => {
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");
    const company = searchParams.get("company");
    const weekOffset = searchParams.get("weekOffset");
    const marketingCode = searchParams.get("marketingCode");

    // Validate custom dates
    const hasValidCustomDates = isValidDate(dateFrom) && isValidDate(dateTo);

    // Validate week offset
    const validWeekOffset = isValidWeekOffset(weekOffset) ? parseInt(weekOffset) : 0;

    // Validate company
    const validCompany = isValidCompany(company) ? company : "All";

    // Validate marketing code
    const validMarketingCode = isValidMarketingCode(marketingCode) ? marketingCode : "All";

    // Priority: custom dates override week offset
    if (hasValidCustomDates) {
      return {
        isCustomRange: true,
        customDateRange: [dayjs.tz(dateFrom, "Europe/Amsterdam"), dayjs.tz(dateTo, "Europe/Amsterdam")],
        weekOffset: 0,
        companyFilter: validCompany,
        marketingCodeFilter: validMarketingCode,
        activeTimeFilter: "custom",
      };
    } else {
      // Use week offset (or default to current week)
      const currentWeek = dayjs().tz("Europe/Amsterdam").add(validWeekOffset, "week");
      return {
        isCustomRange: false,
        customDateRange: null,
        weekOffset: validWeekOffset,
        companyFilter: validCompany,
        marketingCodeFilter: validMarketingCode,
        activeTimeFilter: validWeekOffset === 0 ? "current_week" : validWeekOffset < 0 ? "previous_week" : "next_week",
      };
    }
  };

  // Initialize state from URL or defaults
  const [filters, setFilters] = useState(() => parseFiltersFromURL());

  // Calculate current week based on offset
  const getCurrentWeek = () => {
    return dayjs().tz("Europe/Amsterdam").add(filters.weekOffset, "week");
  };

  // Calculate date range for data fetching
  const dateRange = useMemo(() => {
    if (filters.isCustomRange && filters.customDateRange) {
      return [filters.customDateRange[0], filters.customDateRange[1]];
    } else {
      const currentWeek = dayjs().tz("Europe/Amsterdam").add(filters.weekOffset, "week");
      const start = currentWeek.clone().startOf("isoWeek");
      const end = currentWeek.clone().endOf("isoWeek");
      return [start, end];
    }
  }, [filters.isCustomRange, filters.customDateRange, filters.weekOffset]);

  // Get readable date range text
  const dateRangeText = useMemo(() => {
    const start = dateRange[0];
    const end = dateRange[1];
    return start.format("D MMM") + " - " + end.format("D MMM YYYY");
  }, [dateRange]);

  // Update URL parameters
  const updateURLParams = (newFilters) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Clear all filter params first
    newSearchParams.delete("dateFrom");
    newSearchParams.delete("dateTo");
    newSearchParams.delete("weekOffset");
    newSearchParams.delete("company");
    newSearchParams.delete("marketingCode");

    // Set new params based on filter type
    if (newFilters.isCustomRange && newFilters.customDateRange) {
      newSearchParams.set("dateFrom", newFilters.customDateRange[0].format("YYYY-MM-DD"));
      newSearchParams.set("dateTo", newFilters.customDateRange[1].format("YYYY-MM-DD"));
    } else {
      if (newFilters.weekOffset !== 0) {
        newSearchParams.set("weekOffset", newFilters.weekOffset.toString());
      }
    }

    // Set company if not default
    if (newFilters.companyFilter !== "All") {
      newSearchParams.set("company", newFilters.companyFilter);
    }

    // Set marketing code if not default
    if (newFilters.marketingCodeFilter !== "All") {
      newSearchParams.set("marketingCode", newFilters.marketingCodeFilter);
    }

    setSearchParams(newSearchParams, { replace: true });
  };

  // Filter update functions
  const onChangeRange = (value) => {
    const newFilters = {
      ...filters,
      isCustomRange: !!value && value.length === 2,
      customDateRange: value && value.length === 2 ? value : null,
      weekOffset: 0, // Reset week offset when using custom range
      activeTimeFilter: value && value.length === 2 ? "custom" : "current_week",
    };

    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const onPreviousWeek = () => {
    const newFilters = {
      ...filters,
      isCustomRange: false,
      customDateRange: null,
      weekOffset: filters.weekOffset - 1,
      activeTimeFilter: "previous_week",
    };

    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const onNextWeek = () => {
    const newFilters = {
      ...filters,
      isCustomRange: false,
      customDateRange: null,
      weekOffset: filters.weekOffset + 1,
      activeTimeFilter: "next_week",
    };

    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const onCurrentWeek = () => {
    const newFilters = {
      ...filters,
      isCustomRange: false,
      customDateRange: null,
      weekOffset: 0,
      activeTimeFilter: "current_week",
    };

    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const handleCompanyChange = (value) => {
    const newFilters = {
      ...filters,
      companyFilter: value,
    };

    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const handleMarketingCodeChange = (value) => {
    const newFilters = {
      ...filters,
      marketingCodeFilter: value,
    };

    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      isCustomRange: false,
      customDateRange: null,
      weekOffset: 0,
      companyFilter: "All",
      marketingCodeFilter: "All",
      activeTimeFilter: "current_week",
    };

    setFilters(defaultFilters);
    updateURLParams(defaultFilters);
  };

  // Re-parse URL params when they change (e.g., browser back/forward)
  useEffect(() => {
    const newFilters = parseFiltersFromURL();

    // Only update if filters actually changed to prevent infinite loop
    setFilters((prevFilters) => {
      const hasChanged =
        prevFilters.isCustomRange !== newFilters.isCustomRange ||
        prevFilters.weekOffset !== newFilters.weekOffset ||
        prevFilters.companyFilter !== newFilters.companyFilter ||
        prevFilters.marketingCodeFilter !== newFilters.marketingCodeFilter ||
        prevFilters.customDateRange?.[0]?.format("YYYY-MM-DD") !==
          newFilters.customDateRange?.[0]?.format("YYYY-MM-DD") ||
        prevFilters.customDateRange?.[1]?.format("YYYY-MM-DD") !==
          newFilters.customDateRange?.[1]?.format("YYYY-MM-DD");

      return hasChanged ? newFilters : prevFilters;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Check if filters are at default values
  const isFiltersDefault = () => {
    return (
      !filters.isCustomRange &&
      filters.weekOffset === 0 &&
      filters.companyFilter === "All" &&
      filters.marketingCodeFilter === "All"
    );
  };

  return {
    // State
    isCustomRange: filters.isCustomRange,
    customDateRange: filters.customDateRange,
    companyFilter: filters.companyFilter,
    marketingCodeFilter: filters.marketingCodeFilter,
    activeTimeFilter: filters.activeTimeFilter,

    // Computed values
    currentWeek: getCurrentWeek(),
    dateRange: dateRange,
    dateRangeText: dateRangeText,
    isFiltersDefault: isFiltersDefault(),

    // Actions
    onChangeRange,
    onPreviousWeek,
    onNextWeek,
    onCurrentWeek,
    handleCompanyChange,
    handleMarketingCodeChange,
    resetFilters,

    // URL building helper for navigation
    getURLParamsString: () => {
      return searchParams.toString();
    },
  };
};

export default useReportFilters;
